import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import { useAuth } from '@/domains/identity/AuthProvider';
import { Spinner } from '@/shared/ui';
import { routes } from '../routes';
import { FeatureLockedPage, ForbiddenPage } from './ErrorPages';

function FullPageSpinner() {
  return (
    <div className="flex h-dvh items-center justify-center">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

/**
 * Requires a session. On failure it remembers where the user was going so login can return
 * them there — being bounced to the dashboard after signing in loses the user's intent.
 */
export function RequireAuth() {
  const { isAuthenticated, isLoading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner />;

  if (!isAuthenticated) {
    return <Navigate to={routes.login} replace state={{ from: location.pathname + location.search }} />;
  }

  // A default password locks the entire app down to one screen — including for admins.
  if (mustChangePassword && location.pathname !== routes.forcedPassword) {
    return <Navigate to={routes.forcedPassword} replace />;
  }

  return <Outlet />;
}

/** Already signed in? Skip the login screen. */
export function RequireAnonymous() {
  const { isAuthenticated, isLoading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner />;
  if (isAuthenticated) {
    if (mustChangePassword) return <Navigate to={routes.forcedPassword} replace />;
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? routes.overview} replace />;
  }
  return <Outlet />;
}

export function RequireAdmin() {
  const { isAdmin } = useAuth();
  return isAdmin ? <Outlet /> : <ForbiddenPage />;
}

/**
 * Gates a feature-flagged area. Renders the locked page rather than redirecting, so the user
 * learns the feature exists and can request it.
 */
export function RequireFeature({ featureKey }: { featureKey: string }) {
  const { hasFeature } = useAuth();
  return hasFeature(featureKey) ? <Outlet /> : <FeatureLockedPage featureKey={featureKey} />;
}

/** Guards a route whose :id segment must be a UUID, so a typo 404s instead of 500ing. */
export function RequireUuidParam({ param = 'id' }: { param?: string }) {
  const params = useParams();
  const value = params[param];
  const isUuid = Boolean(value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value));
  return isUuid ? <Outlet /> : <Navigate to={routes.overview} replace />;
}
